import axios from 'axios';

import { API_URL } from '../config';

// All calls route securely through CRM NestJS Backend Proxy -> tbl_configuration
const getBaseUrl = () => {
  if (API_URL) {
    const clean = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
    return `${clean}/patterns-radar`;
  }

  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1')
  ) {
    return '/api/patterns-radar';
  }
  return '/api/patterns-radar';
};

// Create an Axios instance with CRM auth header
const apiClient = axios.create();

apiClient.interceptors.request.use((config) => {
  config.baseURL = getBaseUrl();
  const crmToken = localStorage.getItem('authToken');
  if (crmToken) {
    config.headers.Authorization = `Bearer ${crmToken.trim()}`;
  }
  return config;
});

const patternsRadarService = {
  getBaseUrl,

  // 0. Health & Config
  checkHealth: async () => {
    const res = await apiClient.get('/healthz');
    return res.data;
  },

  getConfig: async () => {
    const res = await apiClient.get('/config');
    return res.data;
  },

  // 1. Market Data
  getMeta: async () => {
    const res = await apiClient.get('/v1/meta');
    return res.data;
  },

  searchSymbols: async (query, limit = 10) => {
    const res = await apiClient.get('/v1/search', {
      params: { q: query, limit },
    });
    return res.data;
  },

  getAllSymbols: async () => {
    const res = await apiClient.get('/v1/symbols');
    return res.data;
  },

  getInstruments: async (params = {}) => {
    // params: { letter, sector, industry, limit, offset }
    const res = await apiClient.get('/v1/instruments', { params });
    return res.data;
  },

  getSymbolIdentity: async (symbol) => {
    const res = await apiClient.get(`/v1/symbols/${encodeURIComponent(symbol)}`);
    return res.data;
  },

  getSymbolStats: async (symbol, deliverySessions = 20) => {
    const res = await apiClient.get(
      `/v1/symbols/${encodeURIComponent(symbol)}/stats`,
      { params: { delivery_sessions: deliverySessions } }
    );
    return res.data;
  },

  getSymbolPeers: async (symbol, limit = 12) => {
    const res = await apiClient.get(
      `/v1/symbols/${encodeURIComponent(symbol)}/peers`,
      { params: { limit } }
    );
    return res.data;
  },

  getSymbolBars: async (symbol, timeframe = '1d') => {
    const res = await apiClient.get(
      `/v1/symbols/${encodeURIComponent(symbol)}/bars`,
      { params: { timeframe } }
    );
    return res.data;
  },

  getSymbolSeries: async (symbol, expressions = [], options = {}) => {
    // expressions: array of strings e.g. ['ema(21)', 'bb(20,2).upper']
    const params = new URLSearchParams();
    expressions.forEach((expr) => params.append('f', expr));
    params.append('timeframe', options.timeframe || '1d');
    if (options.as_of) params.append('as_of', options.as_of);
    if (options.sessions) params.append('sessions', options.sessions);

    const res = await apiClient.get(
      `/v1/symbols/${encodeURIComponent(symbol)}/series?${params.toString()}`
    );
    return res.data;
  },

  // 2. Screening
  runScan: async (payload) => {
    // payload: { source, universe, asOf, limit, orderBy, orderDir, kinds }
    const res = await apiClient.post('/v1/scan', payload);
    return res.data;
  },

  runHitRate: async (payload) => {
    // payload: { source, universe, sessions, horizons, entryMode, benchmark, detailHorizon, stopLossPct, targetPct }
    const res = await apiClient.post('/v1/hitrate', payload);
    return res.data;
  },

  // 3. Saved Scans
  listSavedScans: async () => {
    const res = await apiClient.get('/v1/scans');
    return res.data;
  },

  saveScan: async (payload) => {
    // payload: { name, source, universe }
    const res = await apiClient.post('/v1/scans', payload);
    return res.data;
  },

  renameScan: async (id, name) => {
    const res = await apiClient.patch(`/v1/scans/${id}`, { name });
    return res.data;
  },

  deleteScan: async (id) => {
    const res = await apiClient.delete(`/v1/scans/${id}`);
    return res.data;
  },

  // 4. Alerts
  listAlerts: async () => {
    const res = await apiClient.get('/v1/alerts');
    return res.data;
  },

  getAlertEvents: async (params = {}) => {
    // params: { scan_id, limit }
    const res = await apiClient.get('/v1/alerts/events', { params });
    return res.data;
  },

  getWebhookDeliveries: async (scanId, limit = 10) => {
    const res = await apiClient.get(`/v1/alerts/${scanId}/deliveries`, {
      params: { limit },
    });
    return res.data;
  },

  setAlert: async (scanId, payload) => {
    // payload: { enabled, trigger_mode, channel_email, webhook_url }
    const res = await apiClient.put(`/v1/scans/${scanId}/alert`, payload);
    return res.data;
  },

  removeAlert: async (scanId) => {
    const res = await apiClient.delete(`/v1/scans/${scanId}/alert`);
    return res.data;
  },

  // 5. Account
  whoAmI: async () => {
    const res = await apiClient.get('/v1/auth/me');
    return res.data;
  },

  getNotificationPreferences: async () => {
    const res = await apiClient.get('/v1/me/notifications');
    return res.data;
  },

  updateNotificationPreferences: async (enabled) => {
    const res = await apiClient.patch('/v1/me/notifications', {
      email_alerts_enabled: enabled,
    });
    return res.data;
  },

  getPlans: async () => {
    try {
      const res = await apiClient.get('/v1/plans');
      return res.data;
    } catch (e) {
      return null;
    }
  },
};

export default patternsRadarService;
